import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, Copy, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SECTION_TYPES, newSection } from "@/website/sectionSchemas";
import { FieldGrid, move } from "@/components/admin/website/Fields";

const AddSection = ({ onAdd, nested, testId }) => {
  const [open, setOpen] = useState(false);
  const types = Object.entries(SECTION_TYPES).filter(([k]) => !nested || !["columns", "hero", "platform_hero"].includes(k));
  return (
    <div className="relative">
      <Button type="button" variant="outline" size="sm" className="w-full border-dashed" onClick={() => setOpen(!open)} data-testid={`${testId}-add-section`}><Plus className="w-4 h-4 mr-1" />Add section</Button>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-lg border bg-white shadow-xl p-1 grid sm:grid-cols-2 gap-1">
          {types.map(([k, t]) => (
            <button key={k} type="button" className="text-left text-sm px-3 py-2 rounded hover:bg-slate-100" onClick={() => { onAdd(newSection(k)); setOpen(false); }} data-testid={`${testId}-add-${k}`}>{t.label}</button>
          ))}
        </div>
      )}
    </div>
  );
};

// Local datetime inputs <-> UTC ISO strings
const toLocal = (iso) => { if (!iso) return ""; const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
export const ScheduleInputs = ({ value, onChange, testId, labels, keys }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {keys.map((k, i) => (
      <label key={k} className="block"><span className="text-xs font-semibold text-slate-600">{labels[i]}</span>
        <Input type="datetime-local" className="mt-1" value={toLocal(value[k])} onChange={(e) => onChange({ [k]: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid={`${testId}-${k}`} /></label>
    ))}
  </div>
);

const SectionCard = ({ s, i, total, onChange, onMove, onDelete, onDuplicate, nested, testId }) => {
  const [open, setOpen] = useState(false);
  const schema = SECTION_TYPES[s.type] || { label: s.type, fields: [] };
  const t = `${testId}-${i}`;
  const summary = s.data?.heading || (s.data?.html || "").replace(/<[^>]+>/g, " ").trim().slice(0, 60);
  const renderColumns = (cols, set, ct) => (
    <div className="grid md:grid-cols-2 gap-3">
      {cols.map((c, ci) => (
        <div key={ci} className="border rounded-lg p-2 bg-slate-50">
          <div className="flex justify-between items-center mb-2"><span className="text-xs font-semibold">Column {ci + 1}</span>
            <Button type="button" size="sm" variant="ghost" onClick={() => set(cols.filter((_, j) => j !== ci))}><Trash2 className="w-3.5 h-3.5" /></Button></div>
          <SectionsEditor sections={c.sections || []} onChange={(secs) => set(cols.map((x, j) => (j === ci ? { sections: secs } : x)))} nested testId={`${ct}-col${ci}`} />
        </div>
      ))}
      {cols.length < 4 && <Button type="button" size="sm" variant="outline" onClick={() => set([...cols, { sections: [] }])}>Add column</Button>}
    </div>
  );
  return (
    <div className={`rounded-xl border bg-white ${s.visible === false ? "opacity-60" : ""}`} data-testid={t}>
      <div className="flex items-center gap-1 px-3 py-2">
        <button type="button" className="flex-1 min-w-0 text-left" onClick={() => setOpen(!open)} data-testid={`${t}-toggle`}>
          <span className="text-xs font-semibold uppercase tracking-wide text-[#2563eb]">{schema.label}</span>
          <span className="block text-sm text-slate-600 truncate">{summary || "\u00a0"}</span>
        </button>
        <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onMove(-1)} disabled={i === 0} data-testid={`${t}-up`}><ArrowUp className="w-4 h-4" /></Button>
        <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onMove(1)} disabled={i === total - 1} data-testid={`${t}-down`}><ArrowDown className="w-4 h-4" /></Button>
        <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onChange({ ...s, visible: s.visible === false })} title="Show / hide" data-testid={`${t}-visibility`}>{s.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</Button>
        {!nested && <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={onDuplicate} title="Duplicate" data-testid={`${t}-duplicate`}><Copy className="w-4 h-4" /></Button>}
        <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => window.confirm("Delete this section?") && onDelete()} data-testid={`${t}-delete`}><Trash2 className="w-4 h-4" /></Button>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div className="border-t p-3 space-y-3">
          <FieldGrid fields={schema.fields} data={s.data || {}} onChange={(d) => onChange({ ...s, data: d })} testId={t} renderColumns={renderColumns} />
          <label className="block"><span className="text-xs font-semibold text-slate-600">Anchor id (for links like /#courses)</span>
            <Input className="mt-1" value={s.data?.anchor || ""} onChange={(e) => onChange({ ...s, data: { ...s.data, anchor: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") } })} data-testid={`${t}-anchor`} /></label>
          <ScheduleInputs value={s} onChange={(v) => onChange({ ...s, ...v })} testId={t} labels={["Show from (optional)", "Hide after (optional)"]} keys={["show_from", "hide_after"]} />
        </div>
      )}
    </div>
  );
};

export const SectionsEditor = ({ sections, onChange, nested = false, testId = "sections" }) => (
  <div className="space-y-2" data-testid={testId}>
    {sections.map((s, i) => (
      <SectionCard key={s.id} s={s} i={i} total={sections.length} nested={nested} testId={testId}
        onChange={(n) => onChange(sections.map((x) => (x.id === s.id ? n : x)))}
        onMove={(d) => onChange(move(sections, i, d))}
        onDelete={() => onChange(sections.filter((x) => x.id !== s.id))}
        onDuplicate={() => { const c = { ...JSON.parse(JSON.stringify(s)), id: newSection(s.type).id }; const a = [...sections]; a.splice(i + 1, 0, c); onChange(a); }} />
    ))}
    <AddSection onAdd={(n) => onChange([...sections, n])} nested={nested} testId={testId} />
  </div>
);
